import { useEffect, useRef, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { GiAllSeeingEye, GiQuillInk, GiSpiralBloom } from "react-icons/gi";
import { InterpretationPanel } from "./InterpretationPanel";
import { guestDreamClient } from "../features/dreams/api/guestDreamClient";

const previewDream = (value) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (text.length <= 180) return text;
  return `${text.slice(0, 180).trim()}…`;
};

const scrollToResult = (element) => {
  if (typeof window === "undefined" || !element) return;
  const headerOffset = window.matchMedia("(max-width: 768px)").matches ? 96 : 112;
  const top = element.getBoundingClientRect().top + window.scrollY - headerOffset;
  window.scrollTo({ top: Math.max(0, top), left: 0, behavior: "smooth" });
};

const getGuestErrorFallback = (lang) =>
  lang === "en"
    ? "The free interpretation could not be completed."
    : "Nie udało się wykonać darmowej interpretacji.";

export const Hero = () => {
  const { lang, t } = useLang();
  const [dream, setDream] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const resultRef = useRef(null);
  const hasResult = Boolean(result);

  useEffect(() => {
    if (!result || !resultRef.current) return;
    window.requestAnimationFrame(() => scrollToResult(resultRef.current));
  }, [result]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dream.trim() || loading || hasResult) return;

    setLoading(true);
    setResult(null);
    setError("");

    try {
      const response = await guestDreamClient.analyze({ dreamText: dream, lang });
      if (!response.ok) {
        setError(response.message || t.hero.guestError || getGuestErrorFallback(lang));
        return;
      }
      setResult(response.reading || null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      className="parchment-texture relative pt-20 pb-28 md:pt-28 md:pb-36"
      data-ui="home-hero"
      data-testid="hero-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center" data-ui="home-hero-inner">
        <div
          className="font-accent text-[11px] uppercase tracking-[0.35em] text-gold-muted flex items-center justify-center gap-3"
          data-ui="home-eyebrow"
          data-testid="hero-eyebrow"
        >
          <GiSpiralBloom className="text-lg" />
          {t.hero.eyebrow}
          <GiSpiralBloom className="text-lg scale-x-[-1]" />
        </div>

        <h1
          className="font-heading text-5xl sm:text-6xl lg:text-7xl text-ink mt-8 leading-[1.05]"
          data-ui="home-title"
          data-testid="hero-title"
        >
          {t.hero.title}{" "}
          <em className="text-gold-muted">{t.hero.titleAccent}</em>
        </h1>

        <p
          className="font-body text-base sm:text-lg text-ink-soft mt-8 max-w-2xl mx-auto leading-relaxed"
          data-ui="home-subtitle"
          data-testid="hero-subtitle"
        >
          {t.hero.subtitle}
        </p>

        <div className="divider-ornate mt-12 max-w-md mx-auto" data-ui="home-divider">
          <GiAllSeeingEye className="text-xl flicker" />
        </div>

        {hasResult ? (
          <div className="mt-10 max-w-2xl mx-auto text-left" data-ui="dream-demo-revealed" data-testid="dream-revealed">
            <div className="ornate-frame bg-parchment-dark/45 p-1" data-ui="dream-demo-revealed-frame">
              <div className="bg-parchment px-6 py-5 md:px-7 md:py-6" data-ui="dream-demo-revealed-card">
                <div className="font-accent text-[10px] uppercase tracking-[0.3em] text-gold-muted">
                  {t.hero.revealedEyebrow}
                </div>
                <p className="mt-3 font-heading italic text-lg md:text-xl leading-relaxed text-ink" data-ui="dream-demo-revealed-text">
                  „{previewDream(dream)}”
                </p>
                <p className="mt-4 font-body italic text-xs md:text-sm text-ink-soft" data-ui="dream-demo-revealed-note">
                  {t.hero.revealedNote}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-12 max-w-2xl mx-auto text-left"
            data-ui="dream-demo-form"
            data-testid="dream-form"
          >
            <div className="ornate-frame bg-parchment-dark/60 p-1" data-ui="dream-demo-frame">
              <div className="relative bg-parchment p-6 md:p-8" data-ui="dream-demo-field">
                <GiQuillInk className="absolute top-4 right-4 text-2xl text-gold-muted/40" />
                <textarea
                  value={dream}
                  onChange={(e) => setDream(e.target.value)}
                  placeholder={t.hero.placeholder}
                  rows={6}
                  disabled={loading}
                  data-ui="dream-demo-input"
                  data-testid="dream-input"
                  className="w-full bg-transparent border-0 outline-none resize-none font-body text-base md:text-lg text-ink placeholder:text-ink/40 leading-relaxed"
                />
              </div>
            </div>

            {error && (
              <div className="mdAlert" data-ui="dream-demo-error" data-testid="dream-demo-error">
                {error}
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-ui="dream-demo-actions">
              <span
                className="font-body italic text-sm text-ink-soft"
                data-ui="dream-demo-hint"
                data-testid="hero-hint"
              >
                {t.hero.hint}
              </span>
              <button
                type="submit"
                disabled={loading || !dream.trim()}
                data-ui="dream-demo-submit"
                data-testid="interpret-submit-btn"
                className="group relative inline-flex items-center gap-3 bg-ink text-gold px-9 py-4 font-accent text-[12px] uppercase tracking-[0.25em] hover:bg-ink-deep hover:text-gold-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent hover:border-gold/40"
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full bg-gold transition-opacity ${
                    loading ? "animate-pulse opacity-100" : "opacity-60"
                  }`}
                />
                {loading ? t.hero.loading : t.hero.submit}
              </button>
            </div>
          </form>
        )}
      </div>

      {(loading || result) && (
        <div ref={resultRef} className="max-w-4xl mx-auto px-6 md:px-10 mt-14" data-ui="dream-demo-result">
          <InterpretationPanel
            loading={loading}
            result={result}
          />
        </div>
      )}
    </section>
  );
};
